import { useChainModal, useConnectModal } from '@rainbow-me/rainbowkit';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/toast';
import { formatEther, fromHex } from 'viem';
import {
  Button,
  VStack,
  Text,
  Container,
  Box,
  HStack,
  Input,
  Link,
  Spinner,
  Icon,
  Center,
} from '@chakra-ui/react';
import tree from '../../data/merkle.json';
import { addresses, claimAbi, requiredChainIds } from '../../utils/contracts';
import Card from '../../components/Card';
import XEDToken from '../../components/XEDToken';
import AboutSection from './AboutSection';
import FAQSection from './FAQSection';
import TimelineSection from './TimelineSection';
import { MdWallet } from 'react-icons/md';
import { ConfirmDialog } from '../../components/ConfirmDialog';

type MerkleTree = {
  merkleRoot: string;
  tokenTotal: string;
  claims: Record<string, { index: number; amount: string; proof: string[] }>;
};

const App = () => {
  const { address, chainId } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { openChainModal } = useChainModal();
  const toast = useToast();

  const isAdmin = false;

  const isWrongNetwork = chainId && !requiredChainIds.includes(chainId);

  const [customAddress, setCustomAddress] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [sendToCustomAddress, setSendToCustomAddress] = useState<boolean>(false);

  const addressToClaim = useMemo(
    () => (sendToCustomAddress ? customAddress : address),
    [sendToCustomAddress, customAddress, address],
  );

  const { writeContractAsync, isPending, data: hash } = useWriteContract();

  const merkleTree = tree as MerkleTree;

  const treeAddresses = useMemo(() => Object.keys(merkleTree.claims), [merkleTree.claims]);

  const holder = useMemo(
    () => treeAddresses.find((wallet) => wallet === addressToClaim),
    [addressToClaim, treeAddresses],
  );

  const isHolder = useMemo(() => holder !== undefined, [holder]);

  const holderInfo = useMemo(
    () => (isHolder ? addressToClaim && merkleTree.claims?.[addressToClaim] : undefined),
    [addressToClaim, isHolder, merkleTree.claims],
  );

  const { data: isClaimed = false, refetch } = useReadContract({
    address: addresses.claim,
    abi: claimAbi,
    functionName: 'isClaimed',
    args: [holderInfo ? holderInfo?.index : -1],
  });

  const {
    isLoading: isConfirming,
    isSuccess: isConfirmed,
    isError,
  } = useWaitForTransactionReceipt({
    hash,
  });

  useEffect(() => {
    if (isConfirmed) {
      refetch();
    }
  }, [isConfirmed, refetch, isError]);

  const amountToClaim = holderInfo
    ? formatEther(fromHex(holderInfo.amount as `0x${string}`, 'bigint'))
    : '0';

  const handleClaim = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!holderInfo) {
        throw new Error('Not eligible to claim $XED');
      }

      await writeContractAsync({
        abi: claimAbi,
        address: addresses.claim,
        functionName: 'claim',
        args: [holderInfo.index, addressToClaim, holderInfo.amount, holderInfo.proof],
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Could not claim $XED',
        description: err.message?.split('.')?.[0] ?? err.message,
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, writeContractAsync, holderInfo, addressToClaim]);

  const handleEarlyStake = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!holderInfo) {
        throw new Error('Not eligible to stake $XED');
      }

      // earlyStake(uint256 index, address account, uint256 amount, bytes32[] merkleProof)

      console.log(holderInfo.index, addressToClaim, holderInfo.amount, holderInfo.proof);

      await writeContractAsync({
        abi: claimAbi,
        address: addresses.claim,
        functionName: 'earlyStake',
        args: [holderInfo.index, addressToClaim, holderInfo.amount, holderInfo.proof],
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Could not early stake $XED',
        description: err.message?.split('.')?.[0] ?? err.message,
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, writeContractAsync, holderInfo, addressToClaim]);

  return (
    <Container pt="0" pb="20" maxW="600px" px="20px">
      <VStack align="start" w="100%" spacing="2">
        <Card>
          <VStack w="100%" align="start" spacing="4">
            <VStack align="start" spacing="0.5" pb="4">
              <Text fontSize="md">$XED Migration</Text>
              <Text fontWeight="light" fontSize="sm" color="whiteAlpha.700">
                Claim the new tokens if you were a $XED holder at snapshot date.
              </Text>
            </VStack>
            <VStack align="start" spacing="0" w="100%">
              <HStack w="100%" justify="space-between">
                <Text fontWeight="light" fontSize="sm" color="whiteAlpha.800">
                  Your address
                </Text>
                {sendToCustomAddress ? (
                  <Button
                    size="sm"
                    bg="transparent"
                    color="white"
                    textDecoration="underline"
                    _hover={{}}
                    _active={{}}
                    onClick={() => setSendToCustomAddress(false)}
                  >
                    Claim your address
                  </Button>
                ) : (
                  <>
                    {isAdmin && (
                      <Button
                        size="sm"
                        bg="transparent"
                        color="white"
                        textDecoration="underline"
                        _hover={{}}
                        _active={{}}
                        onClick={() => setSendToCustomAddress(true)}
                      >
                        Claim another address
                      </Button>
                    )}
                  </>
                )}
              </HStack>
              {sendToCustomAddress ? (
                <Input
                  bg="blackAlpha.200"
                  outline="0"
                  border="0"
                  borderBottom="1px solid"
                  borderColor="whiteAlpha.600"
                  borderRadius="0"
                  px="0"
                  _focusVisible={{ borderColor: 'white' }}
                  _placeholder={{ color: 'whiteAlpha.800' }}
                  size="sm"
                  placeholder="Enter the address to claim"
                  value={customAddress}
                  onChange={(e) => setCustomAddress(e.target.value)}
                />
              ) : (
                <Box maxW={{ base: '280px', md: '100%' }}>
                  <Text fontSize="md" isTruncated>
                    {address ? address : '-'}
                  </Text>
                </Box>
              )}
            </VStack>
            <VStack w="100%">
              <HStack w="100%" justify="space-between" pb="4">
                <VStack align="start" spacing="0">
                  <Text fontWeight="light" fontSize="sm" color="whiteAlpha.800">
                    {isClaimed ? 'You have claimed' : 'You can claim'}
                  </Text>
                  <Box maxW={{ base: '180px', md: '100%' }}>
                    <Text fontSize={{ base: 'md', md: 'md' }} isTruncated>
                      {address ? amountToClaim : '-'}
                    </Text>
                  </Box>
                </VStack>
                <XEDToken />
              </HStack>
              {!address ? (
                <Button
                  borderRadius="full"
                  w="100%"
                  bg="brand.500"
                  _hover={{ bg: 'brand.400' }}
                  color="white"
                  onClick={openConnectModal}
                >
                  <HStack spacing="1.5">
                    <Icon as={MdWallet} fontSize="xl" />
                    <Text>Connect Wallet</Text>
                  </HStack>
                </Button>
              ) : isWrongNetwork ? (
                <Button
                  w="100%"
                  borderRadius="full"
                  bg="red.500"
                  _hover={{ bg: 'red.600' }}
                  onClick={openChainModal}
                >
                  Switch network to Ethereum
                </Button>
              ) : (
                <HStack w="100%">
                  <ConfirmDialog
                    onConfirm={handleClaim}
                    confirmText="Claim $XED"
                    title="Claim $XED"
                    description="By claiming $XED you will not be able to early stake and get 2x rewards. Only claim if you do not want to early stake."
                    cancelText="Cancel"
                  >
                    <Button
                      borderRadius="full"
                      w="100%"
                      bg="brand.500"
                      _hover={{ bg: 'brand.400' }}
                      color="white"
                      onClick={handleClaim}
                      isDisabled={(Boolean(isClaimed) || !isHolder) ?? false}
                    >
                      <HStack spacing="2">
                        {(isLoading || isPending || isConfirming) && !isClaimed && (
                          <Spinner boxSize="3.5" />
                        )}
                        <Text fontSize="md">
                          {isPending && 'Accept in your wallet...'}
                          {isConfirming && 'Waiting for confirmation...'}
                          {!isLoading && !isPending && !isConfirming && (
                            <>
                              {isClaimed
                                ? '$XED already claimed'
                                : !isHolder
                                ? 'Wallet not $XED holder'
                                : 'Claim $XED'}
                            </>
                          )}
                        </Text>
                      </HStack>
                    </Button>
                  </ConfirmDialog>
                  {!isClaimed && isHolder && !isLoading && !isPending && !isConfirming && (
                    <Text px="0.5" fontSize="sm">
                      or
                    </Text>
                  )}
                  {!isClaimed && isHolder && !isPending && !isLoading && !isConfirming && (
                    <ConfirmDialog
                      onConfirm={handleEarlyStake}
                      confirmText="Early Stake"
                      title="Early Stake"
                      description="By early staking you will earn 2x rewards. You can unstake anytime."
                      cancelText="Cancel"
                    >
                      <Button
                        w="100%"
                        borderRadius="full"
                        bg="brand.500"
                        _hover={{ bg: 'brand.400' }}
                        color="white"
                      >
                        Early Stake $XED
                      </Button>
                    </ConfirmDialog>
                  )}
                </HStack>
              )}
            </VStack>
          </VStack>
        </Card>

        {hash && (
          <Center w="100%">
            <Text
              w="100%"
              p="3"
              bg="blackAlpha.400"
              borderRadius="lg"
              textAlign="center"
              fontSize="sm"
              as={Link}
              textDecoration="underline"
              href={`https://etherscan.io/tx/${hash}`}
              target="_blank"
            >
              View transaction on Etherscan: {hash?.slice(0, 12)}...{hash?.slice(-12)}
            </Text>
          </Center>
        )}
      </VStack>

      <AboutSection />
      <TimelineSection />
      <FAQSection />
    </Container>
  );
};

export default App;
